/**
 * Search parties (10 §2, missions 24 and 27): dormant men who appear at the edge of the map when their
 * `searchPartyArrives` event fires. The sim sends them straight for the objective's focus; but its path
 * search is bounded, and a focus that is the middle of a wreck cannot be stood on at all - so every party
 * also carries a beat of its own that leads from where it appears to the place, round it a few times and
 * back. Whichever way they go, they get there.
 */

import { atan2 } from '../../sim/math';
import type { EnemyKind, SpawnDef } from '../../sim/types';
import { NO_STAND } from '../builder';
import { FLAG } from '../grid';
import type { Point } from '../types';
import type { GoalCtx } from './types';

const MAX_LEG = 20;

export interface SearchPartyOptions {
  /** Where they are going, and the half-extents of whatever stands there (0, 0 for a man in hiding). */
  focus: Point;
  half: { x: number; z: number };
  /** How far outside that they walk their round. */
  standOff: number;
  men: number;
  kind: EnemyKind;
  tag: string;
  laps: number;
  /** Which of the edges they may come from is tried in order of distance; nearer than this is too sudden. */
  minStart?: number;
  /** The n-th party of a mission takes the n-th best edge, so that waves do not all come the same way. */
  wave?: number;
}

const gap = (a: Point, b: Point): number => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));

/** The edge of the map they walk in from: the nearest that is not on top of the place, nor of the pickup. */
function startFor(g: GoalCtx, o: SearchPartyOptions): Point {
  const f = o.focus;
  const edges: Point[] = [{ x: 3.5, z: f.z }, { x: g.ctx.sizeX - 3.5, z: f.z }, { x: f.x, z: 3.5 }];
  const fair = edges.filter((p) => gap(p, f) >= (o.minStart ?? 30) && gap(p, g.ctx.plan.pickup) >= 25);
  const pool = fair.length > 0 ? fair : edges;
  // Nearest of the fair ones; with none fair, the farthest there is.
  pool.sort((a, b) => (fair.length > 0 ? gap(a, f) - gap(b, f) : gap(b, f) - gap(a, f)));
  return pool[(o.wave ?? 0) % pool.length] ?? edges[0] ?? f;
}

/** The party, or null (and the attempt marked) when their beat cannot be stood on. */
export function searchParty(g: GoalCtx, o: SearchPartyOptions): SpawnDef[] | null {
  const f = o.focus;
  const start = startFor(g, o);
  const [rx, rz] = [o.half.x + o.standOff, o.half.z + o.standOff];
  const ring: Point[] = [{ x: f.x - rx, z: f.z + rz }, { x: f.x + rx, z: f.z + rz }, { x: f.x + rx, z: f.z - rz }, { x: f.x - rx, z: f.z - rz }];
  let first = 0;
  ring.forEach((p, i) => { if (gap(p, start) < gap(ring[first] ?? p, start)) first = i; });
  const entry = ring[first] ?? f;
  const rough: Point[] = [];
  const pieces = Math.max(1, Math.ceil(gap(start, entry) / MAX_LEG));
  for (let i = 0; i < pieces; i++) rough.push({ x: start.x + ((entry.x - start.x) * i) / pieces, z: start.z + ((entry.z - start.z) * i) / pieces });
  const approach = rough.length;
  for (let lap = 0; lap < o.laps; lap++) for (let i = 0; i < 4; i++) rough.push(ring[(first + i) % 4] ?? entry);
  rough.push(entry);

  const out: SpawnDef[] = [];
  for (let m = 0; m < o.men; m++) {
    const nodes: Array<[number, number]> = [];
    let missed = 0;
    rough.forEach((p, i) => {
      const spot = g.map.nearestOpen(p.x + (m % 2) * 1.4, p.z + m * 1.1, 6, NO_STAND);
      if (spot) nodes.push(spot);
      // A corner of the round that falls in a ravine or off the map is walked past, not a reason to scrap the map;
      // a point of the approach that cannot be stood on is, because the leg across it may be too long to path.
      else if (i < approach) missed++;
    });
    if (missed > 0 || nodes.length < approach + 3) {
      g.map.problems.push(`${g.ctx.spec.id}: the search party cannot get to ${Math.round(f.x)}, ${Math.round(f.z)}`);
      return null;
    }
    // Home the way they came, so the loop's closing leg is a short one.
    const patrol = [...nodes];
    for (let i = approach - 1; i >= 1; i--) {
      const node = nodes[i];
      if (node) patrol.push(node);
    }
    const [a, b] = patrol;
    if (!a || !b) continue;
    out.push(g.map.spawn({
      kind: o.kind, x: a[0], z: a[1], facing: atan2(b[0] - a[0], b[1] - a[1]), patrol, dormantUntil: 'timedEvent', wakeTag: o.tag,
    }));
    // Every man's nodes stay standable whatever the budget buys afterwards; the lead's legs stay clear of mines.
    for (const [x, z] of nodes) g.map.mask.circle(x, z, 1.2, FLAG.keep);
    if (m === 0) g.map.mask.line(nodes, 1.5, FLAG.path);
  }
  return out;
}
